import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import {
  Tag,
  Building2,
  Users as UsersIcon,
  GitBranch,
  Bell,
  Folder,
  FileText,
  ShieldCheck,
  Archive,
  Mail,
  BookOpen,
  Files,
} from "lucide-react";

const SECTIONS = [
  { to: "/admin/taxonomy", label: "Taxonomy", icon: Tag },
  { to: "/admin/service-lines", label: "Service Lines", icon: Building2 },
  { to: "/admin/hr-partners", label: "HR Partners", icon: UsersIcon },
  { to: "/admin/workflow", label: "Workflow", icon: GitBranch },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/doc-categories", label: "Document Categories", icon: Folder },
  { to: "/admin/templates", label: "Templates", icon: FileText },
  { to: "/admin/users", label: "Users", icon: ShieldCheck },
  // Phase 10
  { to: "/admin/retention", label: "Retention", icon: Archive },
  // Phase 17
  { to: "/admin/email-outbox", label: "Email Outbox", icon: Mail, gateOutbox: true, adminOnly: true, badge: "outbox" },
  { to: "/admin/runbook", label: "Runbook", icon: BookOpen, adminOnly: true },
  // Phase 18 — admin-only document library + TM file export
  { to: "/admin/documents", label: "Documents", icon: Files, adminOnly: true },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const isAdmin = user?.role === "hr" && user?.is_admin === true;
  const isCompliance = user?.role === "compliance";
  const [outbox, setOutbox] = useState({ visible: false, pending: 0 });

  // Phase 17 — visibility gate + pending count for the Email Outbox badge.
  // Only admins call this; compliance users skip it (the route is HR-only).
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const visRes = await api.get("/admin/email-outbox/visibility");
        if (cancelled) return;
        if (!visRes.data?.visible) {
          setOutbox({ visible: false, pending: 0 });
          return;
        }
        const listRes = await api.get(
          "/admin/email-outbox?status=pending&limit=1",
        );
        if (cancelled) return;
        setOutbox({
          visible: true,
          pending: listRes.data?.pending_count || 0,
        });
      } catch {
        if (!cancelled) setOutbox({ visible: false, pending: 0 });
      }
    };
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin && !isCompliance) return <Navigate to="/cases" replace />;
  const readOnly = !isAdmin && isCompliance;

  const sections = SECTIONS.filter((s) => {
    if (s.adminOnly && !isAdmin) return false;
    if (s.gateOutbox && !outbox.visible) return false;
    return true;
  });

  return (
    <div data-testid="admin-layout" className="mx-auto flex max-w-7xl gap-6 px-6 py-8">
      <aside className="w-60 shrink-0">
        <div className="brand-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2
              className="font-serif text-base font-semibold"
              style={{ color: "var(--page-fg)" }}
            >
              Admin
            </h2>
            {readOnly && (
              <span
                data-testid="admin-readonly-pill"
                className="rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: "rgba(94,171,69,0.10)",
                  color: "#3F7E2D",
                  border: "1px solid rgba(94,171,69,0.30)",
                }}
              >
                Read
              </span>
            )}
          </div>
          <nav className="flex flex-col gap-px">
            {sections.map((s) => {
              const Icon = s.icon;
              const tid = s.to.replace("/admin/", "admin-nav-");
              const showBadge = s.badge === "outbox" && outbox.pending > 0;
              return (
                <NavLink
                  key={s.to}
                  to={s.to}
                  data-testid={tid}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                      isActive ? "" : "hover:bg-[color:var(--surface-2)]"
                    }`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? "var(--page-fg)" : "var(--page-fg-secondary)",
                    backgroundColor: isActive ? "var(--surface-2)" : "transparent",
                    fontWeight: isActive ? 500 : 400,
                  })}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r"
                          style={{ backgroundColor: "#5EAB45" }}
                        />
                      )}
                      <Icon
                        className="h-4 w-4 flex-shrink-0"
                        strokeWidth={1.6}
                        style={{
                          color: isActive ? "#5EAB45" : "var(--page-fg-muted)",
                        }}
                      />
                      <span className="flex-1 truncate text-[13px]">{s.label}</span>
                      {showBadge && (
                        <span
                          data-testid="admin-nav-outbox-badge"
                          className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-md px-1 text-[10px] font-semibold tabular-nums text-white"
                          style={{ backgroundColor: "var(--warning-fg)" }}
                        >
                          {outbox.pending > 99 ? "99+" : outbox.pending}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <Outlet context={{ readOnly, outbox }} />
      </div>
    </div>
  );
}
